import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { UpgradePrompt, UsageMeter, usePlanStatus } from "../components/PlanGate";
import { toast } from "sonner";
import { Search, Plus, Trash2, MapPin, Hash, Building, Lock, Zap, Loader2 } from "lucide-react";
import { BackToDashboard } from "../components/BackToDashboard";
import { Skeleton } from "../components/ui/skeleton";

const BOROUGHS = ["Manhattan", "Brooklyn", "Queens", "Bronx", "Staten Island"];
const JOB_TYPES = ["New Building", "Alteration", "Plumbing", "Electrical", "Demolition", "Sign"];
const NORMALIZE_BOROUGH = { "manhattan": "Manhattan", "brooklyn": "Brooklyn", "queens": "Queens", "bronx": "Bronx", "staten island": "Staten Island" };
const errorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return detail?.message || fallback;
};

const SEARCH_MODES = [
  { id: "address", label: "Address", icon: MapPin, placeholder: "Start typing… e.g. 103 4th Avenue" },
  { id: "bin", label: "BIN", icon: Building, placeholder: "7-digit Building Identification Number" },
  { id: "job_number", label: "Job #", icon: Hash, placeholder: "DOB Job filing number e.g. B00893028" },
];

export default function AddJob() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nickname: "", address: "", borough: "Manhattan", zip: "", job_type: "Alteration",
    bin: "", bbl: "", block: "", lot: "", resolved_address: "", tracked_job_numbers: [],
  });
  const [searchMode, setSearchMode] = useState("address");
  const [searchValue, setSearchValue] = useState("");
  const [searchResult, setSearchResult] = useState(null); // { active_job, jobs, permits, total_jobs_found }
  const [selectedJobNumbers, setSelectedJobNumbers] = useState([]);
  const [foundPermits, setFoundPermits] = useState([]);
  const [manualPermits, setManualPermits] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Address autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // NYC GeoSearch autocomplete (only used when search mode = address)
  const fetchSuggestions = async (text) => {
    try {
      const r = await api.get("/buildings/search", { params: { q: text } });
      setSuggestions(r.data.buildings || []);
      setShowSuggestions(true);
      setActiveIdx(-1);
    } catch (e) { /* silently ignore */ }
  };

  const onSearchValueChange = (v) => {
    setSearchValue(v);
    if (searchMode === "address") update("address", v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (searchMode !== "address") { setShowSuggestions(false); return; }
    if (v.trim().length < 3) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 200);
  };

  const pickSuggestion = (feat) => {
    const p = feat.properties || feat || {};
    const street = [p.housenumber || p.house_no, p.street || p.street_name].filter(Boolean).join(" ").trim() || (p.address || p.name || p.label || "").split(",")[0];
    const boroughRaw = (p.borough || p.locality || "").toLowerCase();
    const borough = NORMALIZE_BOROUGH[boroughRaw] || form.borough;

    // Extract BIN from GeoSearch response — most reliable DOB lookup key.
    // GeoSearch embeds BIN in addendum.pad.bin (or pad_bin at the top level).
    const bin =
      (p.addendum && p.addendum.pad && p.addendum.pad.bin) ||
      p.pad_bin ||
      p.bin ||
      null;
    const bbl =
      (p.addendum && p.addendum.pad && p.addendum.pad.bbl) ||
      p.pad_bbl ||
      p.bbl ||
      null;

    setForm(f => ({
      ...f,
      address: street,
      resolved_address: p.label || street,
      borough,
      zip: p.postalcode || p.zip || f.zip,
      bin: bin || f.bin,
      bbl: bbl || f.bbl,
    }));
    setSearchValue(street);
    setShowSuggestions(false);
    setSuggestions([]);

    if (bin) {
      // Use BIN for exact DOB lookup — avoids street name abbreviation mismatches
      findPermits({ bin, bbl, address: street, mode: "bin" });
    } else {
      // Fallback to address if BIN not available
      findPermits({ address: street, mode: "address" });
    }
  };

  const applySelectedJobsToForm = (jobs) => {
    const primary = jobs[0];
    if (!primary) return;
    setForm(f => ({
      ...f,
      address: primary.address || f.address,
      borough: NORMALIZE_BOROUGH[(primary.borough || "").toLowerCase()] || f.borough,
      zip: primary.zip || f.zip,
      bin: primary.bin || f.bin,
      bbl: primary.bbl || f.bbl,
      block: primary.block || f.block,
      lot: primary.lot || f.lot,
      tracked_job_numbers: jobs.map(j => j.job_number).filter(Boolean),
    }));
  };

  const applySelectedJobs = (jobs, selectedNumbers) => {
    const selected = jobs.filter(j => selectedNumbers.includes(j.job_number));
    setFoundPermits(selected.flatMap(j =>
      (j.permits || []).map(p => ({ ...p, _selected: true, _job_number: j.job_number }))
    ));
    if (selected.length === 0) {
      setForm(f => ({ ...f, tracked_job_numbers: [] }));
      return;
    }
    applySelectedJobsToForm(selected);
  };

  const findPermits = async (override) => {
    const payload = override || (() => {
      const v = searchValue.trim();
      if (!v) return null;
      if (searchMode === "address") return { address: v, mode: "address" };
      if (searchMode === "bin") return { bin: v, mode: "bin" };
      return { job_number: v, mode: "job_number" };
    })();
    if (!payload) { toast.error("Enter a search value"); return; }

    setSearching(true);
    try {
      const r = await api.post("/permits/find", payload);
      const data = r.data;
      setSearchResult(data);
      const jobs = data.jobs || [];
      const defaultSelected = jobs.map(j => j.job_number).filter(Boolean);
      const permits = jobs.flatMap(j => j.permits || []);
      setSelectedJobNumbers(defaultSelected);
      applySelectedJobs(jobs, defaultSelected);

      if (data.jobs?.length === 0 || data.total_jobs_found === 0) {
        toast.info(data.message || "No currently active permits found for this address.");
      } else {
        toast.success(`Found ${jobs.length} active job${jobs.length === 1 ? "" : "s"} · ${permits.length} permit${permits.length === 1 ? "" : "s"} from NYC Open Data`);
      }
    } catch (e) {
      toast.error(errorMessage(e, "Permit search failed"));
    } finally { setSearching(false); }
  };

  const toggleJobSelection = (jobNumber) => {
    const jobs = searchResult?.jobs || [];
    const next = selectedJobNumbers.includes(jobNumber)
      ? selectedJobNumbers.filter(n => n !== jobNumber)
      : [...selectedJobNumbers, jobNumber];
    setSelectedJobNumbers(next);
    applySelectedJobs(jobs, next);
  };

  const setAllJobSelections = (checked) => {
    const jobs = searchResult?.jobs || [];
    const next = checked ? jobs.map(j => j.job_number).filter(Boolean) : [];
    setSelectedJobNumbers(next);
    applySelectedJobs(jobs, next);
  };

  const onSearchKeyDown = (e) => {
    if (searchMode !== "address" || !showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter") { e.preventDefault(); findPermits(); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pickSuggestion(suggestions[activeIdx]); }
    else if (e.key === "Escape") { setShowSuggestions(false); }
  };

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset suggestions when switching mode
  useEffect(() => {
    setShowSuggestions(false);
    setSuggestions([]);
  }, [searchMode]);

  const addManual = () => setManualPermits(p => [...p, { permit_number: "", permit_type: "Building", status: "Pending Review" }]);
  const updateManual = (i, k, v) => setManualPermits(p => p.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const [upgradeMsg, setUpgradeMsg] = useState(null);
  const { status: planStatus, loading: planLoading } = usePlanStatus();

  const removeManual = (i) => setManualPermits(p => p.filter((_, idx) => idx !== i));
  const toggleFound = (i) => setFoundPermits(p => p.map((x, idx) => idx === i ? { ...x, _selected: !x._selected } : x));

  // Determine if the trial has expired and the user has no paid plan
  const trialExpired =
    planStatus &&
    planStatus.plan === "free" &&
    planStatus.trial?.expired === true &&
    !planStatus.trial?.in_trial;

  if (planLoading) return <AddJobSkeleton />;

  // ── Trial-expired wall ───────────────────────────────────────────────────
  if (!planLoading && trialExpired) {
    return (
      <div className="px-4 sm:px-8 py-8 max-w-4xl mx-auto" data-testid="add-job-page">
        <BackToDashboard />
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-8">Add a construction job</h1>

        <div
          className="relative border border-zinc-200 bg-white rounded-sm overflow-hidden"
          data-testid="trial-expired-wall"
        >
          {/* Blurred / dimmed preview of the form behind the overlay */}
          <div className="pointer-events-none select-none opacity-30 blur-[2px] p-6 space-y-5">
            <div className="h-9 w-1/2 bg-zinc-200 rounded-sm" />
            <div className="h-9 w-full bg-zinc-100 rounded-sm" />
            <div className="grid grid-cols-3 gap-4">
              <div className="h-9 bg-zinc-100 rounded-sm" />
              <div className="h-9 bg-zinc-100 rounded-sm" />
              <div className="h-9 bg-zinc-100 rounded-sm" />
            </div>
            <div className="h-9 w-full bg-zinc-100 rounded-sm" />
          </div>

          {/* Overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-white/80 backdrop-blur-sm px-6 py-12">
            <div className="flex items-center justify-center w-14 h-14 rounded-full bg-zinc-950">
              <Lock size={24} className="text-amber-400" />
            </div>
            <div className="text-center max-w-sm">
              <h2 className="font-display text-xl font-bold tracking-tight text-zinc-950 mb-2">
                Your free trial has ended
              </h2>
              <p className="text-sm text-zinc-600 leading-relaxed">
                Adding new jobs and searching buildings requires an active subscription. Upgrade to keep tracking permits and stay on top of your sites.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 w-full max-w-sm text-center">
              {[
                { name: "Starter", price: "$19", jobs: "5 jobs" },
                { name: "Pro", price: "$39", jobs: "25 jobs", highlight: true },
                { name: "Agency", price: "$89", jobs: "Unlimited" },
              ].map(p => (
                <div
                  key={p.name}
                  className={`border rounded-sm p-3 ${p.highlight ? "border-zinc-900 bg-zinc-950 text-white" : "border-zinc-200"}`}
                >
                  <div className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${p.highlight ? "text-amber-400" : "text-zinc-500"}`}>{p.name}</div>
                  <div className={`text-base font-bold ${p.highlight ? "text-white" : "text-zinc-900"}`}>{p.price}<span className="text-xs font-normal">/mo</span></div>
                  <div className={`text-xs mt-0.5 ${p.highlight ? "text-zinc-300" : "text-zinc-500"}`}>{p.jobs}</div>
                </div>
              ))}
            </div>
            <a
              href="/account?tab=billing"
              className="inline-flex items-center gap-2 bg-amber-400 text-zinc-950 font-semibold px-6 py-2.5 rounded-sm hover:bg-amber-300 transition-colors text-sm"
              data-testid="trial-expired-upgrade-cta"
            >
              <Zap size={15} /> Choose a plan to continue
            </a>
          </div>
        </div>
      </div>
    );
  }

  const save = async () => {
    if (saving) return;
    if (!form.nickname || !form.address) { toast.error("Nickname and address are required"); return; }
    setSaving(true);
    try {
      const permits = [
        // Strip only frontend selection helpers; the backend imports these in the background.
        ...foundPermits
          .filter(p => p._selected)
          .map(({ _selected, _job_number, ...rest }) => rest),
        ...manualPermits.filter(p => p.permit_number),
      ];
      const jobRes = await api.post("/jobs", { ...form, permits });
      const job_id = jobRes.data.job_id;
      toast.success("Job created. Loading permit data in the background.");
      navigate(`/jobs/${job_id}`, { state: { importStarted: Boolean(permits.length || form.bin || form.tracked_job_numbers?.length) } });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail?.code === "trial_expired") {
        setUpgradeMsg("Your free trial has ended. Subscribe to a plan to add jobs and permits.");
      } else if (e?.response?.status === 403 && detail?.code) {
        setUpgradeMsg(detail.message || "You've reached your plan limit. Upgrade to continue.");
      } else {
        toast.error("Save failed");
      }
    }
    finally { setSaving(false); }
  };

  const activeMode = SEARCH_MODES.find(m => m.id === searchMode);
  const jobsFound = searchResult?.jobs || [];
  const selectedJobs = jobsFound.filter(j => selectedJobNumbers.includes(j.job_number));
  const selectedFilings = selectedJobs.flatMap(j => (j.filings || []).map(f => ({ ...f, _job_number: j.job_number })));
  const selectedJobIdx = 0;
  const onPickJob = () => {};

  return (
    <div className="px-4 sm:px-8 py-8 max-w-4xl mx-auto" data-testid="add-job-page">
      {saving && <SavingOverlay />}
      {upgradeMsg && <UpgradePrompt message={upgradeMsg} onClose={() => setUpgradeMsg(null)} />}
      <BackToDashboard />
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-8">Add a construction job</h1>
      {planStatus && (
        <div className="mb-6">
          <UsageMeter
            current={planStatus.usage?.jobs || 0}
            max={planStatus.limits?.max_jobs}
            label="Active jobs"
          />
        </div>
      )}

      <div className="border border-zinc-200 bg-white p-6 rounded-sm space-y-5">
        <Field label="Job nickname" required>
          <input
            value={form.nickname}
            onChange={e => update("nickname", e.target.value)}
            placeholder="e.g. Smith Bathroom Renovation"
            className="input-base"
            data-testid="job-nickname-input"
          />
        </Field>

        {/* Search-by toggle */}
        <div>
          <label className="label-eyebrow block mb-1.5">Find permits by</label>
          <div className="inline-flex border border-zinc-200 rounded-sm overflow-hidden" data-testid="search-mode-toggle">
            {SEARCH_MODES.map(m => {
              const Icon = m.icon;
              const active = searchMode === m.id;
              return (
                <button
                  key={m.id} type="button"
                  onClick={() => { setSearchMode(m.id); setSearchValue(""); setSearchResult(null); setFoundPermits([]); }}
                  data-testid={`search-mode-${m.id}`}
                  className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 border-r border-zinc-200 last:border-r-0 ${active ? "bg-zinc-950 text-white" : "bg-white hover:bg-zinc-50"}`}
                >
                  <Icon size={12} /> {m.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-zinc-500 mt-1">We only return <strong>currently active</strong> permits — no expired or revoked filings from years ago.</p>
        </div>

        <div ref={wrapRef} className="relative">
          <label className="label-eyebrow block mb-1.5">
            {activeMode.label} {searchMode === "address" && <span className="text-red-500">*</span>}
          </label>
          <div className="relative flex gap-2">
            <input
              value={searchValue}
              onChange={e => onSearchValueChange(e.target.value)}
              onKeyDown={onSearchKeyDown}
              onFocus={() => { if (searchMode === "address" && suggestions.length) setShowSuggestions(true); }}
              placeholder={activeMode.placeholder}
              className="input-base flex-1"
              data-testid={`search-input-${searchMode}`}
              autoComplete="off"
            />
            <button
              onClick={() => findPermits()}
              disabled={searching}
              data-testid="find-permits-button"
              className="inline-flex items-center gap-1.5 bg-amber-500 text-zinc-950 px-4 py-2 rounded-sm text-sm font-semibold hover:bg-amber-400 disabled:opacity-50 whitespace-nowrap"
            >
              <Search size={14} /> {searching ? "Searching…" : "Search"}
            </button>
          </div>
        {searching && (
            <div className="mt-2 border border-amber-200 bg-amber-50 px-3 py-3 rounded-sm" data-testid="permit-search-loading">
              <div className="flex items-center gap-2 text-xs font-semibold text-amber-900">
                <Loader2 size={13} className="animate-spin" />
                Checking DOB NOW permits, job filings, and legacy BIS records...
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3">
                <Skeleton className="h-4 w-full bg-amber-200/70" />
                <Skeleton className="h-4 w-full bg-amber-200/70" />
                <Skeleton className="h-4 w-2/3 bg-amber-200/70" />
              </div>
            </div>
          )}
          {searchMode === "address" && showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 border border-zinc-200 bg-white rounded-sm shadow-lg max-h-64 overflow-y-auto" data-testid="address-suggestions">
              {suggestions.map((s, i) => (
                <button
                  key={s.bin || s.bbl || s.label || i}
                  type="button"
                  onClick={() => pickSuggestion(s)}
                  onMouseEnter={() => setActiveIdx(i)}
                  data-testid={`address-suggestion-${i}`}
                  className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 border-b border-zinc-100 last:border-b-0 ${i === activeIdx ? "bg-zinc-50" : "hover:bg-zinc-50"}`}
                >
                  <MapPin size={12} className="mt-1 text-zinc-400 flex-shrink-0" />
                  <span className="flex-1 font-medium text-zinc-950">{s.label || s.address}</span>
                </button>
              ))}
            </div>
          )}
          {searchMode === "address" && (
            <p className="mt-1 text-xs text-zinc-500">Powered by NYC GeoSearch — pick an address and we'll auto-fetch its active permits.</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Borough">
            <select value={form.borough} onChange={e => update("borough", e.target.value)} className="input-base" data-testid="job-borough-select">
              {BOROUGHS.map(b => <option key={b}>{b}</option>)}
            </select>
          </Field>
          <Field label="ZIP">
            <input value={form.zip} onChange={e => update("zip", e.target.value)} placeholder="11201" className="input-base" data-testid="job-zip-input" />
          </Field>
          <Field label="Job type">
            <select value={form.job_type} onChange={e => update("job_type", e.target.value)} className="input-base" data-testid="job-type-select">
              {JOB_TYPES.map(j => <option key={j}>{j}</option>)}
            </select>
          </Field>
        </div>

        {/* Resolved address — always visible, editable */}
        <Field label="Street address" required>
          <input
            value={form.address}
            onChange={e => update("address", e.target.value)}
            placeholder="103 4th Avenue"
            className="input-base"
            data-testid="job-address-input"
          />
        </Field>
      </div>

      {searchResult && jobsFound.length > 0 && (
        <div className="border border-amber-200 bg-amber-50 p-4 rounded-sm mt-6" data-testid="multi-job-picker">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-xs font-semibold text-amber-900">
              We found {jobsFound.length} active DOB job{jobsFound.length === 1 ? "" : "s"} at this location.
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-900 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedJobNumbers.length === jobsFound.length}
                onChange={e => setAllJobSelections(e.target.checked)}
                className="accent-zinc-950"
              />
              Select all
            </label>
          </div>
          <div className="space-y-1.5">
            {jobsFound.map((j, i) => {
              const selected = selectedJobNumbers.includes(j.job_number);
              const permitCount = (j.permits || []).length;
              const filingCount = (j.filings || []).length;
              return (
                <label key={j.job_number} className={`flex items-center gap-3 p-2 rounded-sm cursor-pointer ${selected ? "bg-white border border-zinc-300" : "hover:bg-white/60"}`} data-testid={`pick-job-${i}`}>
                  <input type="checkbox" checked={selected} onChange={() => toggleJobSelection(j.job_number)} className="accent-zinc-950" />
                  <div className="flex-1 text-sm">
                    <div className="font-mono font-semibold">Job #{j.job_number}</div>
                    <div className="text-xs text-zinc-600">
                      {j.address} - {permitCount} permit{permitCount === 1 ? "" : "s"}, {filingCount} filing{filingCount === 1 ? "" : "s"} - updated {j.latest_filing_date?.slice(0, 10) || "n/a"}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Job picker (if multiple active jobs at same address/BIN) */}
      {false && searchResult && (searchResult.jobs || []).length > 1 && (
        <div className="border border-amber-200 bg-amber-50 p-4 rounded-sm mt-6" data-testid="multi-job-picker">
          <div className="text-xs font-semibold text-amber-900 mb-2">
            We found {searchResult.jobs.length} active jobs at this location — pick the one you're working on:
          </div>
          <div className="space-y-1.5">
            {searchResult.jobs.map((j, i) => (
              <label key={j.job_number} className={`flex items-center gap-3 p-2 rounded-sm cursor-pointer ${selectedJobIdx === i ? "bg-white border border-zinc-300" : "hover:bg-white/60"}`} data-testid={`pick-job-${i}`}>
                <input type="radio" name="job-pick" checked={selectedJobIdx === i} onChange={() => onPickJob(i)} />
                <div className="flex-1 text-sm">
                  <div className="font-mono font-semibold">Job #{j.job_number}</div>
                  <div className="text-xs text-zinc-600">{j.address} · {j.permits.length} active permit{j.permits.length === 1 ? "" : "s"} · filed {j.latest_filing_date?.slice(0, 10) || "—"}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="border border-zinc-200 bg-white p-6 rounded-sm mt-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
          <div>
            {searchResult?.active_job && (
              <>
                <span className="label-eyebrow">Permits found</span>
                <h2 className="font-display text-lg font-bold tracking-tight mt-1">Attach permits</h2>
                <p className="text-sm text-zinc-600 mt-0.5">
                  Showing active permits for <strong>Job #{(searchResult.jobs?.[selectedJobIdx] || searchResult.active_job).job_number}</strong> — uncheck any you don't want to track.
                </p>
              </>
            )}
          </div>
        </div>

        {foundPermits.length > 0 && (
          <div className="mb-4">
            <div className="label-eyebrow mb-2">Active permits</div>
            <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-100">
              {foundPermits.map((p, i) => (
                <label key={i} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-zinc-50" data-testid={`found-permit-${i}`}>
                  <input type="checkbox" checked={p._selected} onChange={() => toggleFound(i)} className="accent-zinc-950" />
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-12 gap-2 text-sm">
                    <span className="font-mono sm:col-span-4 truncate" title={p.permit_number}>{p.permit_number}</span>
                    <span className="text-zinc-600 sm:col-span-3">{p.permit_type}</span>
                    <span className="text-zinc-600 sm:col-span-3">{p.status}</span>
                    <span className="text-zinc-400 text-xs sm:col-span-2 hidden sm:inline">
                      {p.expiration_date ? `exp ${(p.expiration_date || "").slice(0, 10)}` : ""}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {selectedFilings.length > 0 && (
          <div className="mb-4">
            <div className="label-eyebrow mb-2">Active plan filings</div>
            <div className="border border-zinc-200 rounded-sm divide-y divide-zinc-100">
              {selectedFilings.slice(0, 8).map((f, i) => (
                <div key={f.filing_number || i} className="px-4 py-2.5 text-sm grid grid-cols-1 sm:grid-cols-12 gap-2">
                  <span className="font-mono sm:col-span-4 truncate" title={f.filing_number}>{f.filing_number}</span>
                  <span className="text-zinc-700 sm:col-span-3">{f.filing_status}</span>
                  <span className="text-zinc-600 sm:col-span-3">{f.filing_type || "DOB NOW filing"}</span>
                  <span className="text-zinc-400 text-xs sm:col-span-2 hidden sm:inline">
                    {(f.current_status_date || f.filing_date || "").slice(0, 10)}
                  </span>
                </div>
              ))}
            </div>
            {foundPermits.length === 0 && (
              <p className="text-xs text-zinc-500 mt-2">
                This job can be saved now; we will keep checking these filings until permits are issued.
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <div className="label-eyebrow">Manual entries</div>
          <button onClick={addManual} data-testid="add-manual-permit" className="text-xs font-semibold inline-flex items-center gap-1 text-zinc-700 hover:text-zinc-950">
            <Plus size={12} /> Add row
          </button>
        </div>
        {manualPermits.map((p, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 mb-2" data-testid={`manual-permit-${i}`}>
            <input value={p.permit_number} onChange={e => updateManual(i, "permit_number", e.target.value)} placeholder="Permit #" className="input-base col-span-5" />
            <select value={p.permit_type} onChange={e => updateManual(i, "permit_type", e.target.value)} className="input-base col-span-3">
              {["Building", "Electrical", "Plumbing", "Mechanical", "Sprinkler"].map(t => <option key={t}>{t}</option>)}
            </select>
            <select value={p.status} onChange={e => updateManual(i, "status", e.target.value)} className="input-base col-span-3">
              {["Pending Review", "Approved", "Inspection Scheduled", "Denied"].map(s => <option key={s}>{s}</option>)}
            </select>
            <button onClick={() => removeManual(i)} className="col-span-1 text-zinc-400 hover:text-red-600 flex items-center justify-center"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          onClick={() => navigate(-1)}
          disabled={saving}
          className="px-4 py-2.5 text-sm border border-zinc-200 rounded-sm bg-white hover:bg-zinc-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button onClick={save} disabled={saving} data-testid="save-job-button"
          className="px-5 py-2.5 text-sm font-semibold bg-zinc-950 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-70 inline-flex items-center gap-2">
          {saving && <Loader2 size={15} className="animate-spin" />}
          {saving ? "Creating job..." : "Save Job"}
        </button>
      </div>

      <style>{`
        .input-base { width: 100%; padding: 8px 10px; border: 1px solid #E4E4E7; border-radius: 2px; font-size: 14px; background: #fff; }
        .input-base:focus { outline: 2px solid #09090B; outline-offset: 1px; }
      `}</style>
    </div>
  );
}

function SavingOverlay() {
  return (
    <div className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center px-4" data-testid="saving-job-overlay">
      <div className="border border-zinc-200 bg-white rounded-sm shadow-lg px-6 py-5 max-w-sm w-full text-center">
        <Loader2 size={28} className="animate-spin mx-auto text-zinc-950" />
        <h2 className="font-display text-lg font-bold tracking-tight mt-3">Creating your job</h2>
        <p className="text-sm text-zinc-600 mt-1">
          Processing the permits and job details. This can take a moment.
        </p>
      </div>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="label-eyebrow block mb-1.5">{label}{required && <span className="text-red-500"> *</span>}</label>
    {children}
  </div>
);

function AddJobSkeleton() {
  return (
    <div className="px-4 sm:px-8 py-8 max-w-4xl mx-auto" data-testid="add-job-skeleton">
      <Skeleton className="h-4 w-28 mb-5" />
      <Skeleton className="h-10 w-72 mb-8" />
      <Skeleton className="h-16 w-full mb-6" />
      <div className="border border-zinc-200 bg-white p-6 rounded-sm space-y-5">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-10 w-full" />
        <div>
          <Skeleton className="h-4 w-24 mb-2" />
          <div className="flex gap-2">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-9 w-24" />)}
          </div>
        </div>
        <div>
          <Skeleton className="h-4 w-20 mb-2" />
          <div className="flex gap-2">
            <Skeleton className="h-10 flex-1" />
            <Skeleton className="h-10 w-28" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i}>
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-10 w-full" />
            </div>
          ))}
        </div>
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="border border-zinc-200 bg-white p-6 rounded-sm mt-6">
        <Skeleton className="h-4 w-32 mb-4" />
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 py-2">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-28" />
          </div>
        ))}
      </div>
    </div>
  );
}
